/**
 * FloatingShareRail - sticky left-side vertical share bar for blog posts.
 *
 * Visible only on lg+ screens (hidden on mobile - MobileShareFAB handles mobile).
 * Appears after the user scrolls past 300 px.
 * Disappears when the SharePostRow sentinel at the bottom of the article
 * scrolls into view (so it does not overlap the end-of-article share section).
 *
 * Buttons: LinkedIn, X/Twitter, copy-link
 * GA4: fires trackBlogShareClick + trackOutboundSocialClick on every click.
 */

import { useEffect, useRef, useState } from "react";
import { Check, Link2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import {
  trackBlogShareClick,
  trackOutboundSocialClick,
} from "@/lib/analytics";

// ── LinkedIn icon (inline SVG) ────────────────────────────────────────────────
function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  );
}

// ── X / Twitter icon (inline SVG) ────────────────────────────────────────────
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.747l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────
interface FloatingShareRailProps {
  title: string;
  slug: string;
  /** Optional excerpt used as the tweet body */
  excerpt?: string;
  /**
   * Ref to the SharePostRow sentinel element at the bottom of the article.
   * When the sentinel enters the viewport the rail hides itself so it does not
   * overlap the end-of-article share section.
   */
  sentinelRef?: React.RefObject<HTMLElement | null>;
}

const BASE_URL = "https://integrated.social";

// ── Component ─────────────────────────────────────────────────────────────────
export function FloatingShareRail({
  title,
  slug,
  excerpt,
  sentinelRef,
}: FloatingShareRailProps) {
  const [visible, setVisible] = useState(false);
  const [hiddenBySentinel, setHiddenBySentinel] = useState(false);
  const [copied, setCopied] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const url = `${BASE_URL}/blog/${slug}/`;

  // ── Show/hide based on scroll position ──────────────────────────────────────
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener("scroll", onScroll, { passive: true });
    // Run once on mount in case the page is already scrolled
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── Hide when SharePostRow sentinel is visible ───────────────────────────────
  useEffect(() => {
    if (!sentinelRef) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        setHiddenBySentinel(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, [sentinelRef]);

  const isShown = visible && !hiddenBySentinel;

  // ── Share handlers ───────────────────────────────────────────────────────────
  const handleLinkedIn = () => {
    trackBlogShareClick({ platform: "linkedin", post_slug: slug, post_title: title });
    trackOutboundSocialClick({
      social_platform: "linkedin",
      link_location: "floating_share_rail",
      destination_url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    });
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleX = () => {
    const tweetText = excerpt
      ? `${excerpt.slice(0, 200)}… `
      : `${title} `;
    const tweetUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(url)}&via=MoElnadi`;
    trackBlogShareClick({ platform: "twitter", post_slug: slug, post_title: title });
    trackOutboundSocialClick({
      social_platform: "twitter",
      link_location: "floating_share_rail",
      destination_url: tweetUrl,
    });
    window.open(tweetUrl, "_blank", "noopener,noreferrer");
  };

  const handleWhatsApp = () => {
    const message = excerpt
      ? `${title}\n\n${excerpt.slice(0, 150)}\n\n${url}`
      : `${title}\n\n${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    trackBlogShareClick({ platform: "whatsapp", post_slug: slug, post_title: title });
    trackOutboundSocialClick({
      social_platform: "whatsapp",
      link_location: "floating_share_rail",
      destination_url: whatsappUrl,
    });
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for older browsers
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopied(true);
    toast.success("Link copied to clipboard");
    trackBlogShareClick({ platform: "copy_link", post_slug: slug, post_title: title, share_method: "icon_button" });
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      role="complementary"
      aria-label="Share this article"
      style={{
        opacity: isShown ? 1 : 0,
        pointerEvents: isShown ? "auto" : "none",
        transform: isShown ? "translateX(0)" : "translateX(-8px)",
        transition: "opacity 0.25s cubic-bezier(0.23,1,0.32,1), transform 0.25s cubic-bezier(0.23,1,0.32,1)",
      }}
      className={[
        // Hidden on mobile and tablet - MobileShareFAB handles those breakpoints
        "hidden lg:flex",
        // Positioning: fixed to the viewport left edge, vertically centred
        "fixed left-4 top-1/2 -translate-y-1/2 z-40",
        // Layout: vertical column of buttons
        "flex-col items-center gap-2",
      ].join(" ")}
      data-testid="desktop-sticky-share-rail"
    >
      {/* "Share" label */}
      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1 select-none">
        Share
      </span>

      {/* LinkedIn */}
      <button
        onClick={handleLinkedIn}
        aria-label="Share on LinkedIn"
        title="Share on LinkedIn"
        className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#0A66C2] text-white hover:bg-[#004182] active:scale-95 transition-all duration-150 shadow-sm shadow-[#0A66C2]/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0A66C2]"
      >
        <LinkedInIcon className="w-4 h-4" />
      </button>

      {/* X / Twitter */}
      <button
        onClick={handleX}
        aria-label="Share on X (Twitter)"
        title="Share on X"
        className="w-9 h-9 rounded-lg flex items-center justify-center bg-foreground text-background hover:bg-foreground/80 active:scale-95 transition-all duration-150 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground"
      >
        <XIcon className="w-3.5 h-3.5" />
      </button>

      {/* WhatsApp */}
      <button
        onClick={handleWhatsApp}
        aria-label="Share on WhatsApp"
        title="Share on WhatsApp"
        className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#25D366] text-white hover:bg-[#1da851] active:scale-95 transition-all duration-150 shadow-sm shadow-[#25D366]/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#25D366]"
      >
        <MessageCircle className="w-4 h-4" aria-hidden="true" />
      </button>

      {/* Copy link */}
      <button
        onClick={handleCopyLink}
        aria-label={copied ? "Link copied!" : "Copy link"}
        title={copied ? "Copied!" : "Copy link"}
        className={[
          "w-9 h-9 rounded-lg flex items-center justify-center active:scale-95 transition-all duration-150 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
          copied
            ? "bg-amber-500 text-slate-900 focus-visible:outline-amber-500"
            : "bg-muted text-muted-foreground hover:bg-muted/70 focus-visible:outline-border",
        ].join(" ")}
      >
        {copied ? (
          <Check className="w-4 h-4" />
        ) : (
          <Link2 className="w-4 h-4" />
        )}
      </button>
    </div>
  );
}
